import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, Linkedin, Github, Instagram, Copy } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { z } from "zod";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message must be less than 2000 characters"),
});

const Contact = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate form data
      const validationResult = contactSchema.safeParse(formData);
      
      if (!validationResult.success) {
        const firstError = validationResult.error.errors[0];
        toast({
          title: "Validation Error",
          description: firstError.message,
          variant: "destructive",
        });
        return;
      }
      
      // Create mailto link with validated data
      const mailtoLink = `mailto:widyo1107@gmail.com?subject=Portfolio Contact from ${encodeURIComponent(validationResult.data.name)}&body=${encodeURIComponent(
        `Name: ${validationResult.data.name}\nEmail: ${validationResult.data.email}\n\nMessage:\n${validationResult.data.message}`
      )}`;
      
      // Open mailto link
      window.location.href = mailtoLink;
      
      toast({
        title: "Opening email client...",
        description: "Your default email client will open with the message.",
      });
      
      // Reset form after a short delay
      setTimeout(() => {
        setFormData({ name: "", email: "", message: "" });
      }, 1000);
    } catch (error) {
      console.error("Error opening email client:", error);
      toast({
        title: "Error",
        description: "Could not open email client. Please send email directly to widyo1107@gmail.com",
        variant: "destructive",
      });
    }
  };

  const handleEmailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      window.location.href = "mailto:widyo1107@gmail.com";
      toast({
        title: "Opening email client...",
        description: "Your default email client will open.",
      });
    } catch (error) {
      console.error("Error opening email client:", error);
      toast({
        title: "Email Address",
        description: "widyo1107@gmail.com",
      });
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("widyo1107@gmail.com");
    toast({
      title: "Copied!",
      description: "Email address copied to clipboard",
    });
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText("+6281111110744");
    toast({
      title: "Copied!",
      description: "Phone number copied to clipboard",
    });
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">{t.contact.title}</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7 md:gap-8 max-w-5xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6 animate-slide-in-left scroll-reveal-left">
            <Card className="shadow-soft hover-glow transition-smooth">
              <CardHeader>
                <CardTitle>{t.contact.info}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">{t.contact.email}</p>
                    <a
                      href="mailto:widyo1107@gmail.com"
                      onClick={handleEmailClick}
                      className="text-foreground hover:text-primary transition-colors cursor-pointer"
                    >
                      widyo1107@gmail.com
                    </a>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCopyEmail}
                    className="h-8 w-8 flex-shrink-0"
                    aria-label="Copy email address to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">{t.contact.phone}</p>
                    <a
                      href="tel:+6281111110744"
                      className="text-foreground hover:text-primary transition-colors"
                    >
                      +6281111110744
                    </a>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCopyPhone}
                    className="h-8 w-8 flex-shrink-0"
                    aria-label="Copy phone number to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft hover-glow transition-smooth">
              <CardHeader>
                <CardTitle>{t.contact.social}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <a
                  href="https://linkedin.com/in/widyo11"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors group"
                >
                  <Linkedin className="h-5 w-5 text-primary" />
                  <span className="text-foreground group-hover:text-primary transition-colors">
                    LinkedIn
                  </span>
                </a>
                <a
                  href="https://github.com/tcuber11"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors group"
                >
                  <Github className="h-5 w-5 text-primary" />
                  <span className="text-foreground group-hover:text-primary transition-colors">
                    GitHub
                  </span>
                </a>
                <a
                  href="https://www.instagram.com/tcu98"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors group"
                >
                  <Instagram className="h-5 w-5 text-primary" />
                  <span className="text-foreground group-hover:text-primary transition-colors">
                    Instagram
                  </span>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="shadow-soft animate-slide-in-right hover-glow transition-smooth scroll-reveal-right">
            <CardHeader>
              <CardTitle>{t.contact.sendMessage}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="text-sm font-medium mb-2 block">
                    {t.contact.name}
                  </label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder={t.contact.namePlaceholder}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="text-sm font-medium mb-2 block">
                    {t.contact.email}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={!formData.name.trim()}
                    placeholder={t.contact.emailPlaceholder}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="text-sm font-medium mb-2 block">
                    {t.contact.message}
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    disabled={!formData.name.trim() || !formData.email.trim()}
                    placeholder={t.contact.messagePlaceholder}
                    rows={5}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full shadow-soft"
                  disabled={!formData.name.trim() || !formData.email.trim() || !formData.message.trim()}
                >
                  {t.contact.send}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-12 text-sm text-muted-foreground">
          {t.contact.copyright}
        </div>
      </div>
    </section>
  );
};

export default Contact;
